import { useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUiStore } from "@/stores/ui";
import { useTasksStore } from "@/stores/tasks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Task, TaskStatus, TaskIntent } from "@/core/state-shape";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { PasteToTaskButton } from "@/components/PasteToTaskButton";
import { useProjectsStore } from "@/stores/projects";

// As per PROJECT.MD, status has a specific grouping order and intent options
const statusOrder: TaskStatus[] = [
  "reviewing",
  "validating",
  "needs_attention",
  "validated",
  "generating",
  "draft",
  "complete",
  "merged",
  "closed",
  "error",
  "failed",
];

const statusDisplay: Record<TaskStatus, string> = {
    draft: "Draft",
    generating: "Generating",
    reviewing: "Needs Review",
    validating: "Validating",
    validated: "Validated",
    needs_attention: "Needs Attention",
    complete: "Complete",
    merged: "Sprocket",
    closed: "Closed",
    error: "Error",
    failed: "Failed",
}

const intentOptions: TaskIntent[] = ["Add Feature", "Fix Bug", "Refactor", "Update Docs", "Add Tests", "Performance", "Security"];

const TaskCardSkeleton = () => (
    <Card>
        <CardHeader>
            <CardTitle>
                <Skeleton className="h-5 w-3/4" />
            </CardTitle>
            <CardDescription>
                <Skeleton className="h-4 w-1/2" />
            </CardDescription>
        </CardHeader>
        <CardContent>
            <div className="flex flex-wrap gap-2">
                <Skeleton className="h-6 w-20 rounded-md" />
                <Skeleton className="h-6 w-24 rounded-md" />
            </div>
        </CardContent>
        <CardFooter>
            <Skeleton className="h-4 w-1/3" />
        </CardFooter>
    </Card>
);

export default function TasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { byId, list, uiPrefs, setFilter, setSort, toggleSelection, loadTasks, loading } = useTasksStore();
  const { setHeaderTitle, showCompletedTasks, setShowCompletedTasks } = useUiStore();
  const { activeProjectId } = useProjectsStore();

  const { filters, sort, grouping } = uiPrefs;

  useEffect(() => {
    setHeaderTitle("Tasks");
  }, [setHeaderTitle]);

  useEffect(() => {
    if (activeProjectId) {
      loadTasks(activeProjectId);
    }
  }, [activeProjectId, loadTasks]);


  useEffect(() => {
    const q = searchParams.get('q') || '';
    const status = (searchParams.get('status')?.split(',') || []) as TaskStatus[];
    const intent = (searchParams.get('intent')?.split(',') || []) as TaskIntent[];
    const sortParam = searchParams.get('sort')?.split(':');
    const showCompleted = searchParams.get('showCompleted') === 'true';

    // This effect syncs the URL to the state. It should not depend on the state it sets to avoid loops.
    const newFilters: any = { q, status: status.filter(Boolean), intent: intent.filter(Boolean), showCompleted };

    if (q !== (filters as any).q || JSON.stringify(status) !== JSON.stringify(filters.status) || JSON.stringify(intent) !== JSON.stringify(filters.intent) || showCompleted !== !!(filters as any).showCompleted) {
        setFilter(newFilters);
    }

    if (sortParam && (sortParam[0] !== sort.key || sortParam[1] !== sort.direction)) {
        setSort({ key: sortParam[0] as any, direction: sortParam[1] as any });
    }
  }, [searchParams, setFilter, setSort]); // We remove `filters` and `sort` from the dependency array.

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if ((filters as any).q) newParams.set('q', (filters as any).q);
    else newParams.delete('q');

    if (filters.status && filters.status.length > 0) newParams.set('status', filters.status.join(','));
    else newParams.delete('status');

    if (filters.intent && filters.intent.length > 0) newParams.set('intent', filters.intent.join(','));
    else newParams.delete('intent');

    if ((filters as any).showCompleted) newParams.set('showCompleted', 'true');
    else newParams.delete('showCompleted');

    if (sort.key && sort.direction) newParams.set('sort', `${sort.key}:${sort.direction}`);
    else newParams.delete('sort');

    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [filters, sort, searchParams, setSearchParams]);

  const tasks = useMemo(() => {
    let filteredTasks = list.map(id => byId[id]).filter(Boolean) as Task[];

    if (!showCompletedTasks) {
      const completedStatuses: TaskStatus[] = ['complete', 'merged', 'closed'];
      filteredTasks = filteredTasks.filter(t => !completedStatuses.includes(t.status));
    }

    const query = (filters as any).q?.toLowerCase();
    if (query) {
      filteredTasks = filteredTasks.filter(t =>
        t.title?.toLowerCase().includes(query) ||
        t.id.toLowerCase().includes(query) ||
        t.labels.some(l => l.toLowerCase().includes(query))
      );
    }

    if (filters?.status?.length) {
      filteredTasks = filteredTasks.filter(t => filters.status!.includes(t.status));
    }

    if (filters?.intent?.length) {
      filteredTasks = filteredTasks.filter(t => t.intent && filters.intent!.includes(t.intent));
    }

    filteredTasks.sort((a, b) => {
      if (sort.key === 'updatedAt') {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      } else if (sort.key === 'createdAt') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else if (sort.key === 'title' && a.title && b.title) {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
    if (sort.direction === 'asc') {
        filteredTasks.reverse();
    }

    return filteredTasks;
  }, [list, byId, filters, sort, showCompletedTasks]);

  const groupedTasks = useMemo(() => {
    if (!grouping.byStatus) {
      return { 'All Tasks': tasks };
    }
    const groups: Record<string, Task[]> = {};
    for (const task of tasks) {
      const statusLabel = statusDisplay[task.status] || 'Other';
      if (!groups[statusLabel]) {
        groups[statusLabel] = [];
      }
      groups[statusLabel].push(task);
    }
    return groups;
  }, [tasks, grouping.byStatus]);

  const sortedGroups = Object.keys(groupedTasks).sort((a, b) => {
      const statusA = Object.keys(statusDisplay).find(key => statusDisplay[key as TaskStatus] === a) as TaskStatus;
      const statusB = Object.keys(statusDisplay).find(key => statusDisplay[key as TaskStatus] === b) as TaskStatus;
      return statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);
  });
  
  return (
    <div className="container mx-auto p-4 overflow-auto">
      <header className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold truncate max-w-full md:max-w-[33vw]">Tasks</h1>
        </div>
        <div className="flex items-center gap-2">
          <PasteToTaskButton />
          <Button className="w-full sm:w-auto" onClick={() => navigate("/pages/task-new/")}>
            New Task
          </Button>
        </div>
      </header>

      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center">
        <Select 
          value={filters?.status?.[0] ?? "__ALL_STATUS__"}
          onValueChange={(value) => {
            if (value === "__ALL_STATUS__") {
              setFilter({ status: [] });
              return;
            }
            setFilter({ status: value ? [value as TaskStatus] : [] });
          }}
            
>    
        <SelectTrigger className="w-full sm:w-[180px]">
            
                <SelectValue placeholder="Filter by status..." />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__ALL_STATUS__">All Statuses</SelectItem>
                {Object.entries(statusDisplay).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
        <Select
          value={filters?.intent?.[0] ?? "__ALL_INTENTS__"}
          onValueChange={(value) => {
                if (value === "__ALL_INTENTS__") {
                  setFilter({ intent: [] });
                  return;
                }
                setFilter({ intent: value ? [value as TaskIntent] : [] });
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by intent..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL_INTENTS__">All Intents</SelectItem>
            {intentOptions.map(intent => (
              <SelectItem key={intent} value={intent}>{intent}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={`${sort.key}:${sort.direction}`}
          onValueChange={(value) => {
            const [key, direction] = value.split(':');
            setSort({ key: key as any, direction: direction as any });
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
              <SelectItem value="updatedAt:desc">Last Updated</SelectItem>
              <SelectItem value="createdAt:desc">Date Created</SelectItem>
              <SelectItem value="title:asc">Title</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
            <Checkbox
                id="show-completed"
                checked={showCompletedTasks}
                onCheckedChange={(checked) => setShowCompletedTasks(!!checked)}
            />
            <label
                htmlFor="show-completed"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
                Show Completed &amp; Closed
            </label>
        </div>
      </div>

      {loading && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => <TaskCardSkeleton key={i} />)}
          </div>
      )}

      {!loading && tasks.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
            <h2 className="text-xl">No tasks found</h2>
            <p className="mt-2">{filters.status?.length || filters.intent?.length ? "Try adjusting your filters." : "Create your first task to get started."}</p>
            <Button className="mt-4" onClick={() => navigate("/pages/task-new/")}>
                Create Task
            </Button>
        </div>
      )}

      {!loading && tasks.length > 0 && (
        <div className="space-y-8">
            {sortedGroups.map(groupName => (
                groupedTasks[groupName]?.length > 0 && (
                    <section key={groupName}>
                        <h2 className="text-lg font-semibold mb-2">{groupName}</h2>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {groupedTasks[groupName].map(task => (
                                <Card
                                    key={task.id}
                                    className="relative hover:shadow-md transition-shadow w-full max-w-full overflow-hidden"
                                >
                                    <div className="absolute top-4 right-4 z-10">
                                        <Checkbox
                                            onCheckedChange={() => toggleSelection(task.id)}
                                            aria-label={`Select task ${task.title || task.id}`}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div
                                        className="cursor-pointer h-full flex flex-col min-w-0"
                                        onClick={() => navigate(`/pages/task-detail/${task.id}`)}
                                    >
                                        <CardHeader className="min-w-0">
                                            <CardTitle className="truncate">{task.title || `Task ${task.id}`}</CardTitle>
                                            <CardDescription>
                                                Updated {new Date(task.updatedAt).toLocaleDateString()}
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="flex-grow min-w-0">
                                            <div className="flex flex-wrap gap-2">
                                                <Badge variant="secondary">{statusDisplay[task.status] || task.status}</Badge>
                                                {task.intent && <Badge variant="outline">{task.intent}</Badge>}
                                                {task.labels.map(label => (
                                                    <Badge key={label} variant="outline">{label}</Badge>
                                                ))}
                                            </div>
                                        </CardContent>
                                        <CardFooter className="justify-between">
                                            <p className="text-sm text-muted-foreground">{new Date(task.createdAt).toLocaleString()}</p>
                                            <p className="text-sm text-muted-foreground truncate" title={task.id}>{task.id}</p>
                                        </CardFooter>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </section>
                )
            ))}
        </div>
      )}
    </div>
  );
}